/**
 * Values Guidance Component
 *
 * Provides guidance content for setting up and maintaining values.
 * Now powered by Sanity CMS with fallback to API.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Book, Lightbulb, Target, RefreshCw } from 'lucide-react';
import { useGuidanceWithFallback, useSanityStatus } from '@/hooks/use-guidance';
import { GuidanceRenderer } from '@/components/guidance/guidance-renderer';
import { AppLinkButton } from '@/components/guidance/app-link-button';
import type { SanityGuidance, SanityBlock, SanityAppLink } from '@/lib/sanity';

interface ValuesGuidanceProps {
  compact?: boolean;
  defaultExpandedTopic?: string;
}

// Icon mapping for legacy topics
const iconMap: Record<string, React.FC<{ className?: string }>> = {
  book: Book,
  lightbulb: Lightbulb,
  target: Target,
  'refresh-cw': RefreshCw,
};

export function ValuesGuidance({ compact = false, defaultExpandedTopic }: ValuesGuidanceProps) {
  const [expandedTopic, setExpandedTopic] = useState<string | null>(
    compact ? null : (defaultExpandedTopic || 'introduction')
  );
  const { data, isLoading, isSanity } = useGuidanceWithFallback('values');
  const { isConfigured } = useSanityStatus();

  const guidanceItems = data as SanityGuidance[] | undefined;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-stone-200 rounded w-1/3" />
          <div className="h-4 bg-stone-100 rounded w-2/3" />
          <div className="h-4 bg-stone-100 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl border border-primary-100 p-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Lightbulb className="w-5 h-5 text-primary-700" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Values Guide Your Path</h3>
            <p className="text-sm text-stone-600 mt-1">
              Values are your compass for living intentionally. Unlike goals, values are
              directions that guide every step of your journey.
            </p>
            <button
              onClick={() => setExpandedTopic('introduction')}
              className="mt-3 text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              Learn more →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 bg-stone-50 border-b border-stone-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-stone-900 flex items-center gap-2">
              <Book className="w-5 h-5 text-primary-600" />
              Values Guidance
            </h3>
            <p className="text-sm text-stone-500 mt-1">
              Learn how to set up and maintain your values for intentional living.
            </p>
          </div>
          {isConfigured && (
            <span className="text-xs text-stone-400 bg-stone-100 px-2 py-1 rounded">
              CMS
            </span>
          )}
        </div>
      </div>

      {/* Guidance Topics */}
      <div className="divide-y divide-stone-100">
        {guidanceItems && guidanceItems.length > 0 ? (
          guidanceItems.map((item) => (
            <GuidanceTopicItem
              key={item._id}
              item={item}
              isExpanded={expandedTopic === item.topic}
              onToggle={() => setExpandedTopic(
                expandedTopic === item.topic ? null : item.topic
              )}
              isSanity={isSanity}
            />
          ))
        ) : (
          <div className="px-5 py-8 text-center">
            <p className="text-stone-500">No guidance content available.</p>
            {!isConfigured && (
              <p className="text-sm text-stone-400 mt-2">
                Configure Sanity CMS to add rich guidance content.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual guidance topic item with expand/collapse
 */
interface GuidanceTopicItemProps {
  item: SanityGuidance;
  isExpanded: boolean;
  onToggle: () => void;
  isSanity: boolean;
}

function GuidanceTopicItem({ item, isExpanded, onToggle, isSanity }: GuidanceTopicItemProps) {
  const IconComponent = item.icon && iconMap[item.icon] ? iconMap[item.icon] : Book;

  return (
    <div id={`guidance-${item.topic}`}>
      {/* Topic Header */}
      <button
        onClick={onToggle}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-stone-100 rounded-lg">
            <IconComponent className="w-4 h-4 text-stone-500" />
          </div>
          <div className="text-left">
            <span className="font-medium text-stone-700">{item.title}</span>
            {item.summary && (
              <p className="text-xs text-stone-400 mt-0.5 line-clamp-1">
                {item.summary}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-stone-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-5 pb-5">
          <div className="pl-10">
            {/* Rich content from Sanity */}
            {isSanity && item.content && (
              <GuidanceRenderer content={item.content} />
            )}

            {/* Plain text fallback for API content */}
            {!isSanity && item.content && (
              <div className="text-sm text-stone-600 whitespace-pre-line">
                {renderPlainTextContent(item.content)}
              </div>
            )}

            {/* Quick Actions */}
            {item.quickActions && item.quickActions.length > 0 && (
              <div className="mt-5 pt-4 border-t border-stone-100">
                <h4 className="text-xs font-medium text-stone-400 uppercase tracking-wide mb-3">
                  Quick Actions
                </h4>
                <div className="flex flex-wrap gap-2">
                  {item.quickActions.map((action: SanityAppLink) => (
                    <AppLinkButton
                      key={action._key}
                      label={action.label}
                      route={action.route}
                      style={action.style || 'secondary'}
                      icon={action.icon}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Related Topics */}
            {item.relatedGuidance && item.relatedGuidance.length > 0 && (
              <div className="mt-4 pt-4 border-t border-stone-100">
                <h4 className="text-xs font-medium text-stone-400 uppercase tracking-wide mb-2">
                  Related Topics
                </h4>
                <div className="flex flex-wrap gap-2">
                  {item.relatedGuidance.map((related) => (
                    <a
                      key={related._id}
                      href={`#guidance-${related.topic}`}
                      className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      {related.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Render plain text content from legacy API format
 */
function renderPlainTextContent(content: SanityBlock[]): string {
  // Handle legacy format where content is a simple block with text
  if (!content || content.length === 0) return '';

  return content
    .map((block) => {
      if (block.children) {
        return block.children
          .map((child) => child.text || '')
          .join('');
      }
      return '';
    })
    .join('\n\n');
}
